import { useCallback, useRef, useState } from "react";
import { SESSION_DETAILS_URL } from "../constants";
import { normaliseDocs } from "../helpers";

/**
 * Manages the single persistent SSE connection.
 *
 * The stream is opened ONCE on mount and kept alive for the entire page
 * lifecycle. It is NEVER reconnected because filters changed — that is
 * the job of the REST fetch. SSE only reconnects when the connection
 * physically dies (readyState CLOSED after an onerror event).
 *
 * @param {React.MutableRefObject<Map>} sessionsMapRef - shared map from useSessionFetch
 * @param {string} selectedPlatform - current platform filter (for routing updates)
 * @param {Function} setDateWiseData - context setter
 * @param {Function} setplatformWiseFilterData - context setter
 */
export default function useSSE({
  sessionsMapRef,
  selectedPlatform,
  setDateWiseData,
  setplatformWiseFilterData,
}) {
  const esRef = useRef(null);

  const [connStatus, setConnStatus] = useState("idle");
  const [newIds, setNewIds] = useState(new Set());
  const [updatedIds, setUpdatedIds] = useState(new Set());
  const [liveCount, setLiveCount] = useState(0);

  const resetBadges = useCallback(() => {
    setNewIds(new Set());
    setUpdatedIds(new Set());
    setLiveCount(0);
  }, []);

  const openSSE = useCallback(() => {
    if (esRef.current) return; // never open a second stream

    setConnStatus("connecting");

    const es = new EventSource(SESSION_DETAILS_URL, {
      withCredentials: true,
    });
    esRef.current = es;

    es.onopen = () => setConnStatus("live");

    const parsePayload = (raw) => {
      const payload = JSON.parse(raw);
      return { doc: payload.doc ?? payload };
    };

    // ── new_session ─────────────────────────────────────────────────────
    es.addEventListener("new_session", (e) => {
      try {
        const { doc: rawDoc } = parsePayload(e.data);
        const doc = normaliseDocs([rawDoc])[0];

        if (sessionsMapRef.current.has(doc._id)) return;

        sessionsMapRef.current.set(doc._id, doc);

        if (!selectedPlatform || selectedPlatform.length === 0) {
          setDateWiseData((prev) => [doc, ...prev]);
        } else {
          setplatformWiseFilterData((prev) => [doc, ...prev]);
        }

        setNewIds((prev) => new Set([...prev, doc._id]));
        setLiveCount((c) => c + 1);
      } catch (err) {
        console.error("SSE new_session parse error:", err);
      }
    });

    // ── updated_session ─────────────────────────────────────────────────
    es.addEventListener("updated_session", (e) => {
      try {
        const { doc: rawDoc } = parsePayload(e.data);
        const doc = normaliseDocs([rawDoc])[0];

        sessionsMapRef.current.set(doc._id, doc);

        const updateList = (prev) => {
          const idx = prev.findIndex((s) => s._id === doc._id);
          if (idx === -1) return [{ ...doc }, ...prev];
          const next = [...prev];
          next[idx] = { ...doc };
          return next;
        };

        if (!selectedPlatform || selectedPlatform.length === 0) {
          setDateWiseData(updateList);
        } else {
          setplatformWiseFilterData(updateList);
        }

        setNewIds((prev) => {
          const s = new Set(prev);
          s.delete(doc._id);
          return s;
        });
        setUpdatedIds((prev) => new Set([...prev, doc._id]));
      } catch (err) {
        console.error("SSE updated_session parse error:", err);
      }
    });

    // ── Backend error event ─────────────────────────────────────────────
    es.addEventListener("error", (e) => {
      if (e.data) {
        try {
          console.error("SSE backend error:", JSON.parse(e.data)?.error);
        } catch (_) {
          /* empty */
        }
      }
    });

    // ── Transport error ─────────────────────────────────────────────────
    es.onerror = () => {
      if (es.readyState === EventSource.CLOSED) {
        setConnStatus("error");
        esRef.current = null;
      } else {
        setConnStatus("connecting");
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const closeSSE = useCallback(() => {
    if (esRef.current) {
      esRef.current.close();
      esRef.current = null;
    }
    setConnStatus("idle");
  }, []);

  const reconnectSSE = useCallback(() => {
    closeSSE();
    setTimeout(openSSE, 100);
  }, [closeSSE, openSSE]);

  return {
    connStatus,
    newIds,
    updatedIds,
    liveCount,
    openSSE,
    closeSSE,
    reconnectSSE,
    resetBadges,
  };
}
